/**
 * The gunnery pass, lifted out of `encounter-tick.ts` so each of the tick's
 * three weapon passes owns a file — `gun-pass.ts`, `bomb-pass.ts`,
 * `ram-pass.ts` — and the tick itself is left doing nothing but ordering them.
 *
 * Nothing here changed in the move. It is still the pass documented at the top
 * of `encounter-tick.ts`: rounds leave the barrels from the shooter's *pre-step*
 * pose, because that is the picture the pilot was aiming at, and then every
 * round in the air is swept against the pre-step poses of everything it could
 * run into (`collide.ts`). Nothing in it is a probability.
 *
 * ## The airship's rounds ride this pass, and that is deliberate
 *
 * A Zeppelin shoots back (`airship-pass.ts`, `MG.DAT`), and it does it from
 * *inside* this function rather than from a pass of its own. A flak shell got
 * its own pass because a shell is genuinely not a round — it is not swept, it
 * cannot be dodged, and it is resolved where it is created. An airship's rounds
 * are rifle-calibre bullets flying dead straight out of a mount with a traverse
 * arc, which is precisely what a `RoundRecord` already is. Giving them their
 * own pass would have meant a second live list, a second cap, a second sweep
 * and a second thing for the renderer to draw, all of them subtly different
 * from these.
 *
 * So they join `spawned`, they are pruned by the same cap, they are swept by
 * the same `collide.ts`, and `apps/web` draws them without knowing they exist.
 * Two things in here had to learn about a non-aircraft shooter, both marked
 * below: the sweep's per-shooter target lookup, and which RNG stream a hit is
 * rolled on.
 */

import type { RngBank, RngStreamName } from "../flight/rng.ts";
import type { SimState } from "../flight/state.ts";
import type { Tunables } from "../flight/tunables.ts";
import { AIRSHIP_RNG_STREAM, airshipPass, type AirshipBelt, type AirshipQuarry } from "./airship-pass.ts";
import { targetRadiusM } from "./ballistics.ts";
import { sweepRounds, type CollisionTarget } from "./collide.ts";
import type { CombatAircraft } from "./encounter.ts";
import { bodyOf } from "./geometry.ts";
import { hitFromImpact, type RoundHit } from "./hit-allocation.ts";
import { resolveGunnery, roundsFiredBetween, type GunneryTarget } from "./gunnery.ts";
import { opponentsOf } from "./quarry.ts";
import { pruneRounds, roundLifetimeTicks, type RoundRecord } from "./rounds.ts";
import { targetGunneryEntry, type EncounterTarget } from "./targets.ts";

/** One aircraft's pre-step record paired with the state its own `step` produced. */
export interface GunStep {
  readonly entry: CombatAircraft;
  readonly sim: SimState;
}

/** What the pass needs off the roster input — a structural subset of `RosterInput`. */
export interface GunPassInput {
  readonly aircraft: readonly CombatAircraft[];
  readonly targets: readonly EncounterTarget[];
  readonly rounds: readonly RoundRecord[];
  readonly rng: RngBank;
  readonly tick: number;
  readonly dt: number;
}

export interface Volley {
  readonly hits: readonly RoundHit[];
  /** Every round still in the air after this tick's collisions. */
  readonly rounds: readonly RoundRecord[];
  readonly roundsFired: readonly number[];
  /**
   * Belts and round ordinals for every airship that fired, keyed by id — what
   * `settleTargets` writes back onto the target list. Empty on every tick where
   * no Zeppelin's gunner had anything in his arc, which is most of them.
   */
  readonly airshipBelts: ReadonlyMap<string, AirshipBelt>;
}

function targetsFor(input: GunPassInput, entry: CombatAircraft): GunneryTarget[] {
  const aircraft = opponentsOf(input.aircraft, entry).map((other) => ({
    id: other.id,
    body: bodyOf(other.sim),
    targetSizeRaw: other.sim.config.spec.structure.targetSizeRaw,
  }));
  const statics = input.targets
    .filter((other) => other.side !== entry.side && !other.destroyed)
    .map(targetGunneryEntry);
  return [...aircraft, ...statics];
}

/**
 * What an airship's gunners may traverse onto: every aeroplane on the board,
 * with its own side and whether it is still there. Not the target list — an
 * airship does not shoot at balloons, sheds or other airships, and
 * `shooting.c+0x534`'s loop is airships × *aircraft*.
 */
function airshipQuarryOf(aircraft: readonly CombatAircraft[]): AirshipQuarry[] {
  return aircraft.map((entry) => ({
    id: entry.id,
    side: entry.side,
    body: bodyOf(entry.sim),
    destroyed: entry.status === "destroyed",
  }));
}


/** The same target list a shooter's guns were solving for, as spheres for `collide.ts`. */
function asCollisionTargets(targets: readonly GunneryTarget[], tunables: Tunables): CollisionTarget[] {
  return targets.map((target) => ({
    id: target.id,
    body: target.body,
    radiusM: targetRadiusM(target.targetSizeRaw, tunables),
  }));
}

export function gunPass(
  before: readonly CombatAircraft[],
  flown: readonly GunStep[],
  input: GunPassInput,
  tunables: Tunables,
): Volley {
  const spawned: RoundRecord[] = [];
  const roundsFired: number[] = [];
  // One target list per aircraft, shared between the shot it takes this tick
  // and the sweep of everything it has already fired. Built lazily, because a
  // roster where nobody has a round in the air pays nothing for it.
  const targetsByShooter = new Map<string, readonly GunneryTarget[]>();
  const targetsOf = (entry: CombatAircraft): readonly GunneryTarget[] => {
    const cached = targetsByShooter.get(entry.id);
    if (cached) return cached;
    const built = targetsFor(input, entry);
    targetsByShooter.set(entry.id, built);
    return built;
  };

  before.forEach((entry, index) => {
    const stepped = flown[index];
    const fired = stepped ? roundsFiredBetween(entry.sim, stepped.sim) : 0;
    roundsFired.push(fired);
    if (fired <= 0) return;
    const result = resolveGunnery({
      shooter: {
        id: entry.id,
        body: bodyOf(entry.sim),
        gun: entry.sim.config.spec.guns,
        roundsFired: fired,
        roundsBefore: entry.roundsFired,
        alphaRad: entry.sim.readout.alphaRad,
        // The jitter's only switch, and it is the belt rather than the
        // airframe — `gunnery.ts`'s `incendiaryJitterRad` carries the
        // disassembly that corrected it. The value is already on the roster
        // entry, so nothing new is threaded through the sim to reach it.
        incendiary: entry.ammunition === "incendiary",
      },
      targets: targetsOf(entry),
      rng: input.rng,
      tunables: entry.sim.config.tunables,
      tick: input.tick,
    });
    spawned.push(...result.rounds);
  });

  // The airship's four gunners, after the aeroplanes and before the prune, so
  // their rounds are subject to the same cap and the same sweep. They draw off
  // the `airship` stream, so where they sit in this order cannot move a single
  // aeroplane's `gunnery` draw.
  const airship = airshipPass({
    airships: input.targets,
    quarry: airshipQuarryOf(before),
    rng: input.rng,
    tunables,
    tick: input.tick,
    dt: input.dt,
  });
  spawned.push(...airship.rounds);

  // Prune the combined list, not just the carried-over half: the cap has to
  // hold *after* this tick's rounds join, and `pruneRounds` keeps the newest,
  // so an overflowing sky loses its oldest and most distant rounds first.
  const live = pruneRounds([...input.rounds, ...spawned], input.tick, roundLifetimeTicks(tunables, input.dt));
  const spheres = new Map<string, readonly CollisionTarget[]>();
  // Airship spheres are the aeroplanes it was shooting at, built once and
  // shared, exactly as an aeroplane's are. Without this branch a Zeppelin's
  // rounds would fly on forever and hit nothing: the sweep looks its shooter up
  // in the *aircraft* list, and an airship is not in it.
  const airshipSpheres = (airshipId: string): readonly CollisionTarget[] => {
    const shooter = input.targets.find((entry) => entry.id === airshipId);
    if (!shooter) return [];
    return asCollisionTargets(
      input.aircraft
        .filter((entry) => entry.side !== shooter.side && entry.status !== "destroyed")
        .map((entry) => ({
          id: entry.id,
          body: bodyOf(entry.sim),
          targetSizeRaw: entry.sim.config.spec.structure.targetSizeRaw,
        })),
      tunables,
    );
  };
  const swept = sweepRounds({
    rounds: live,
    targetsFor: (shooterId) => {
      const cached = spheres.get(shooterId);
      if (cached) return cached;
      const shooter = input.aircraft.find((entry) => entry.id === shooterId);
      const built = shooter ? asCollisionTargets(targetsOf(shooter), tunables) : airshipSpheres(shooterId);
      spheres.set(shooterId, built);
      return built;
    },
    tick: input.tick,
    dt: input.dt,
    tunables,
  });
  // Which stream a hit is rolled on follows the round's shooter, not the pass:
  // an airship's magnitude and component draws belong to `airship` for the same
  // reason a battery's belong to `flak`.
  const airshipShooters = new Set(airship.rounds.map((round) => round.shooterId));
  const streamOf = (shooterId: string): RngStreamName =>
    airshipShooters.has(shooterId) || input.targets.some((entry) => entry.id === shooterId)
      ? AIRSHIP_RNG_STREAM
      : "gunnery";
  const hits = swept.impacts.map((impact) =>
    hitFromImpact(impact, input.rng, tunables, streamOf(impact.round.shooterId)),
  );
  return { hits, rounds: swept.survivors, roundsFired, airshipBelts: airship.belts };
}
